/**
 * The app: the top bar (03 §0.4), the router with lazy, guarded routes (06 §1.3), error boundary, toasts, the update
 * prompt, the staging ribbon and the impersonation banner. The platform routes exist on both sites, each site's game
 * routes only on it (11 §8.3); tile.game shows non-admins a plain page while it is closed (11 §8.6).
 */
import type { ComponentChildren } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { LocationProvider, lazy, Route, Router, useLocation } from 'preact-iso';
import { publicConfig } from '../auth/config';
import { Toasts } from '../hud/Toasts';
import { initInstallPrompt } from '../pwa/install';
import { UpdatePrompt } from '../pwa/UpdatePrompt';
import { Home } from '../routes/home';
import { NotFound } from '../routes/notFound';
import { TileClosed } from '../sites/tile/closed';
import { authStore } from '../stores/auth';
import { AppErrorBoundary } from './ErrorBoundary';
import { FEATURES } from './features';
import { adminRoute, userRoute } from './guards';
import { ImpersonationBanner } from './ImpersonationBanner';
import { MaintenanceBanner } from './MaintenanceBanner';
import { SiteHeader } from './SiteHeader';
import { StagingRibbon } from './StagingRibbon';
import { SITE } from './site';
import { useBannersHeight } from './siteTop';

// tile.game's pages (no Tarock code)
const TileHome = lazy(() => import('../sites/tile/home').then((m) => m.TileHome));
// 11 §11: the tile games' strings come with their routes
const PlayDominoes = lazy(() =>
  import('../sites/tile/playDominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.PlayDominoes;
  }),
);
const RulesIndex = lazy(() =>
  import('../sites/tile/rulesIndex').then(async (m) => {
    await m.loadTileStrings();
    return m.RulesIndex;
  }),
);
const RulesDominoes = lazy(() =>
  import('../sites/tile/rulesDominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.RulesDominoes;
  }),
);
const PlayTriominoes = lazy(() =>
  import('../sites/tile/playTriominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.PlayTriominoes;
  }),
);
const RulesTriominoes = lazy(() =>
  import('../sites/tile/rulesTriominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.RulesTriominoes;
  }),
);
const PlayQuadominoes = lazy(() =>
  import('../sites/tile/playQuadominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.PlayQuadominoes;
  }),
);
const RulesQuadominoes = lazy(() =>
  import('../sites/tile/rulesQuadominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.RulesQuadominoes;
  }),
);
const PlayHexominoes = lazy(() =>
  import('../sites/tile/playHexominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.PlayHexominoes;
  }),
);
const RulesHexominoes = lazy(() =>
  import('../sites/tile/rulesHexominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.RulesHexominoes;
  }),
);
const PlaySpherominoes = lazy(() =>
  import('../sites/tile/playSpherominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.PlaySpherominoes;
  }),
);
const RulesSpherominoes = lazy(() =>
  import('../sites/tile/rulesSpherominoes').then(async (m) => {
    await m.loadTileStrings();
    return m.RulesSpherominoes;
  }),
);
const PlayTileRummy = lazy(() =>
  import('../sites/tile/playTileRummy').then(async (m) => {
    await m.loadTileStrings();
    return m.PlayTileRummy;
  }),
);
const RulesTileRummy = lazy(() =>
  import('../sites/tile/rulesTileRummy').then(async (m) => {
    await m.loadTileStrings();
    return m.RulesTileRummy;
  }),
);
const PlayMexicanTrain = lazy(() =>
  import('../sites/tile/playMexicanTrain').then(async (m) => {
    await m.loadTileStrings();
    return m.PlayMexicanTrain;
  }),
);
const RulesMexicanTrain = lazy(() =>
  import('../sites/tile/rulesMexicanTrain').then(async (m) => {
    await m.loadTileStrings();
    return m.RulesMexicanTrain;
  }),
);
// tile.game's online pages (P16, ADR 0058): the lobby, the table, the records; each with the tile strings
const TileLobbyPage = userRoute(
  lazy(() =>
    import('../sites/tile/lobby').then(async (m) => {
      await m.loadTileStrings();
      return m.TileLobbyPage;
    }),
  ),
);
const TileTableRoute = userRoute(
  lazy(() =>
    import('../sites/tile/table').then(async (m) => {
      await m.loadTileStrings();
      return m.TileTableRoute;
    }),
  ),
);
const TileHistoryPage = userRoute(
  lazy(() =>
    import('../sites/tile/history').then(async (m) => {
      await m.loadTileStrings();
      return m.TileHistoryPage;
    }),
  ),
);
const TileLeaderboardPage = userRoute(
  lazy(() =>
    import('../sites/tile/leaderboard').then(async (m) => {
      await m.loadTileStrings();
      return m.TileLeaderboardPage;
    }),
  ),
);
const TileStatsPage = userRoute(
  lazy(() =>
    import('../sites/tile/stats').then(async (m) => {
      await m.loadTileStrings();
      return m.TileStatsPage;
    }),
  ),
);
// a replay answers "Replay not found" to everyone it refuses, signed out too (03 §10.3)
const TileReplayRoute = lazy(() =>
  import('../sites/tile/replay').then(async (m) => {
    await m.loadTileStrings();
    return m.TileReplayRoute;
  }),
);
const PlayLocal = lazy(() => import('../routes/playLocal').then((m) => m.PlayLocal));
const Rules = lazy(() => import('../routes/rules').then((m) => m.Rules));
const Credits = lazy(() => import('../routes/credits').then((m) => m.Credits));
const DeckCards = lazy(() => import('../routes/deckCards').then((m) => m.DeckCards));
const Legal = lazy(() => import('../routes/legal').then((m) => m.Legal));
// the auth chunk
const Login = lazy(() => import('../routes/auth').then((m) => m.Login));
const Signup = lazy(() => import('../routes/auth').then((m) => m.Signup));
const Verify = lazy(() => import('../routes/auth').then((m) => m.Verify));
const Reset = lazy(() => import('../routes/auth').then((m) => m.Reset));
const TwoFactor = lazy(() => import('../routes/auth').then((m) => m.TwoFactor));
// the profile chunk
const Welcome = userRoute(lazy(() => import('../routes/profile').then((m) => m.Welcome)));
const ProfilePage = userRoute(lazy(() => import('../routes/profile').then((m) => m.ProfilePage)));
const Security = userRoute(lazy(() => import('../routes/profile').then((m) => m.Security)));
const PlayerInvites = userRoute(lazy(() => import('../routes/profile').then((m) => m.PlayerInvites)));
const StatsPage = userRoute(lazy(() => import('../routes/profile').then((m) => m.StatsPage)));
// the lobby chunk
const LobbyPage = userRoute(lazy(() => import('../routes/lobby').then((m) => m.LobbyPage)));
const JoinPage = userRoute(lazy(() => import('../routes/lobby').then((m) => m.JoinPage)));
// the leaderboard chunk (03 §11.5)
const LeaderboardPage = userRoute(lazy(() => import('../routes/leaderboard').then((m) => m.LeaderboardPage)));
// the history chunk (03 §10); a replay answers "Replay not found" to everyone it refuses, signed out too (§10.3)
const HistoryPage = userRoute(lazy(() => import('../routes/history').then((m) => m.HistoryPage)));
const ReplayRoute = lazy(() => import('../routes/replay').then((m) => m.ReplayRoute));
// the table chunk (online)
const TableRoute = userRoute(lazy(() => import('../routes/table').then((m) => m.TableRoute)));
// the admin chunk: loaded only for admins
const AdminApp = adminRoute(
  lazy(() =>
    import('../routes/admin').then(async (m) => {
      await m.loadAdminStrings(); // ADR 0015: the admin.* strings come with the chunk
      return m.AdminApp;
    }),
  ),
);

// Capture `beforeinstallprompt` from boot; the table chunk loads later (03 §16).
initInstallPrompt();

/**
 * The pages a closed site still serves: signing in (an admin needs it), the legal pages, and local play with its
 * rules, which need no account (Q11: offline play without one). The landing, the lobby and the tables stay gated.
 */
const OPEN_WHILE_CLOSED = [
  '/login',
  '/2fa',
  '/verify',
  '/reset',
  '/legal/',
  '/play/dominoes',
  '/rules/dominoes',
  '/play/triominoes',
  '/rules/triominoes',
  '/play/mexican-train',
  '/rules/mexican-train',
  '/play/quadominoes',
  '/rules/quadominoes',
  '/play/hexominoes',
  '/rules/hexominoes',
  '/play/spherominoes',
  '/rules/spherominoes',
  '/play/tile-rummy',
  '/rules/tile-rummy',
];

/** tile.game while `siteAccess.tile` is `admins`: admins see the site, everyone else the plain page (11 §8.6). */
function SiteGate({ children }: { children: ComponentChildren }) {
  const { path } = useLocation();
  const [access, setAccess] = useState<'admins' | 'open' | null>(SITE === 'tile' ? null : 'open');
  useEffect(() => {
    if (SITE === 'tile') {
      void publicConfig().then((c) => setAccess(c?.siteAccess ?? 'admins'));
    }
  }, []);
  const admin = authStore.user.value?.role === 'admin';
  if (access === 'open' || admin || OPEN_WHILE_CLOSED.some((p) => path.startsWith(p))) {
    return <>{children}</>;
  }
  return access === null || authStore.status.value === 'unknown' ? null : <TileClosed />;
}

/** The game routes of tarock.game (11 §8.3); a route of the other site is a 404. */
const TAROCK_ROUTES =
  SITE === 'tarock'
    ? [
        <Route key="play" path="/play/local" component={PlayLocal} />,
        <Route key="rules" path="/rules" component={Rules} />,
        <Route key="credits" path="/credits" component={Credits} />,
        <Route key="deck" path="/credits/:deck" component={DeckCards} />,
        <Route key="replay" path="/replay/:gameId" component={ReplayRoute} />,
      ]
    : [];

/**
 * The game routes of tile.game (11 §8.3, 16 §11): local dominoes and its rules since P15; the replay of a stored hand
 * since P16; local Triominoes and its rules since P17; local Mexican Train and its rules since P18; local Quadominoes
 * and its rules from P22; local Hexominoes and its rules from P23; local Spherominoes and its rules from P24; the index
 * of the rules at `/rules` (the footer's one Rules link, 2026-10-13).
 */
const TILE_ROUTES =
  SITE === 'tile' && FEATURES.tileSite
    ? [
        <Route key="rules-index" path="/rules" component={RulesIndex} />,
        ...(FEATURES.dominoes
          ? [
              <Route key="play-dominoes" path="/play/dominoes" component={PlayDominoes} />,
              <Route key="rules-dominoes" path="/rules/dominoes" component={RulesDominoes} />,
            ]
          : []),
        ...(FEATURES.triominoes
          ? [
              <Route key="play-triominoes" path="/play/triominoes" component={PlayTriominoes} />,
              <Route key="rules-triominoes" path="/rules/triominoes" component={RulesTriominoes} />,
            ]
          : []),
        ...(FEATURES.mexicanTrain
          ? [
              <Route key="play-mexican-train" path="/play/mexican-train" component={PlayMexicanTrain} />,
              <Route key="rules-mexican-train" path="/rules/mexican-train" component={RulesMexicanTrain} />,
            ]
          : []),
        ...(FEATURES.quadominoes
          ? [
              <Route key="play-quadominoes" path="/play/quadominoes" component={PlayQuadominoes} />,
              <Route key="rules-quadominoes" path="/rules/quadominoes" component={RulesQuadominoes} />,
            ]
          : []),
        ...(FEATURES.hexominoes
          ? [
              <Route key="play-hexominoes" path="/play/hexominoes" component={PlayHexominoes} />,
              <Route key="rules-hexominoes" path="/rules/hexominoes" component={RulesHexominoes} />,
            ]
          : []),
        ...(FEATURES.spherominoes
          ? [
              <Route key="play-spherominoes" path="/play/spherominoes" component={PlaySpherominoes} />,
              <Route key="rules-spherominoes" path="/rules/spherominoes" component={RulesSpherominoes} />,
            ]
          : []),
        ...(FEATURES.tileRummy
          ? [
              <Route key="play-tile-rummy" path="/play/tile-rummy" component={PlayTileRummy} />,
              <Route key="rules-tile-rummy" path="/rules/tile-rummy" component={RulesTileRummy} />,
            ]
          : []),
        <Route key="replay-tile" path="/replay/:game/:id" component={TileReplayRoute} />,
      ]
    : [];

/** The platform pages per site (11 §8.3, ADR 0058): tile.game's lobby, table and records are its own. */
const tile = SITE === 'tile' && FEATURES.tileSite;
const PAGES = {
  lobby: tile ? TileLobbyPage : LobbyPage,
  table: tile ? TileTableRoute : TableRoute,
  history: tile ? TileHistoryPage : HistoryPage,
  leaderboard: tile ? TileLeaderboardPage : LeaderboardPage,
  stats: tile ? TileStatsPage : StatsPage,
};

/** The root component. */
export function App() {
  const banners = useRef<HTMLDivElement>(null);
  useBannersHeight(banners);
  return (
    <LocationProvider>
      <div class="site-top">
        <div class="site-banners" ref={banners}>
          <StagingRibbon />
          <ImpersonationBanner />
          <MaintenanceBanner />
        </div>
        <SiteHeader />
      </div>
      <AppErrorBoundary>
        <SiteGate>
          <Router>
            <Route path="/" component={SITE === 'tile' ? TileHome : Home} />
            {TAROCK_ROUTES}
            {TILE_ROUTES}
            <Route path="/legal/:page" component={Legal} />
            <Route path="/login" component={Login} />
            <Route path="/signup" component={Signup} />
            <Route path="/verify" component={Verify} />
            <Route path="/reset" component={Reset} />
            <Route path="/2fa" component={TwoFactor} />
            <Route path="/welcome" component={Welcome} />
            <Route path="/lobby" component={PAGES.lobby} />
            <Route path="/join/:token" component={JoinPage} />
            <Route path="/t/:id" component={PAGES.table} />
            <Route path="/profile" component={ProfilePage} />
            <Route path="/profile/security" component={Security} />
            <Route path="/profile/invites" component={PlayerInvites} />
            <Route path="/profile/stats" component={PAGES.stats} />
            {FEATURES.leaderboard
              ? [<Route key="leaderboard" path="/leaderboard" component={PAGES.leaderboard} />]
              : []}
            <Route path="/history" component={PAGES.history} />
            <Route path="/admin" component={AdminApp} />
            <Route path="/admin/*" component={AdminApp} />
            <Route default component={NotFound} />
          </Router>
        </SiteGate>
      </AppErrorBoundary>
      <Toasts />
      <UpdatePrompt />
    </LocationProvider>
  );
}
